import {
  Alert,
  Box,
  CircularProgress,
  Container,
  Stack,
  Typography,
} from '@mui/material'
import { Outlet, useParams } from 'react-router-dom'
import { useCurrentStore, useLoadStore, useStoreBizHours } from 'hooks'
import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { KCenteredContentBox } from 'components/core/KCenteredContentBox'

const LoadingError = (error: any) => {
  const { t } = useTranslation()
  return (
    <Stack spacing={2} sx={{ textAlign: 'center', margin: 4 }}>
      <Typography variant="h4" color="error">
        {t('Error loading store.')}
      </Typography>
      <Typography variant="body2">{error?.message}</Typography>
    </Stack>
  )
}

export const StoreLayout = () => {
  const { t } = useTranslation()
  const { storeId, tableId } = useParams()

  const { loadStore, store, isLoading, isError, error } = useLoadStore()
  const { isAcceptingOrder, pauseOrderBefore } = useCurrentStore()
  const { isOpening, nextOpeningTime } = useStoreBizHours()

  useEffect(() => {
    if (storeId) {
      loadStore({ storeId, tableId })
    }
  }, [storeId, tableId, loadStore])

  const storeMessage = useMemo(() => {
    if (isAcceptingOrder === false) {
      if (pauseOrderBefore) {
        return t('Store will accept order again after {{time}}.', {
          time: pauseOrderBefore?.format('hh:mm A'),
        })
      } else {
        return t('Store is not accepting order at the moment.')
      }
    } else if (isOpening === false) {
      if (nextOpeningTime) {
        return t('Store will open again at {{time}}.', {
          time: nextOpeningTime.format('hh:mm A'),
        })
      } else {
        return t('Store is closed today.')
      }
    }
  }, [isAcceptingOrder, pauseOrderBefore, isOpening, nextOpeningTime, t])

  if (isLoading) {
    return (
      <KCenteredContentBox>
        <CircularProgress />
      </KCenteredContentBox>
    )
  }

  if (isError) {
    return <LoadingError error={error} />
  }

  // If store is not loaded or store id is not matched, do not render anything and wait for store to be loaded.
  // This is to prevent the flash of the layout before the store is loaded.
  if (!store || store.id !== storeId) {
    return null
  }

  return (
    <Stack sx={{ height: '100%', overflow: 'hidden' }}>
      {storeMessage && (
        <Container maxWidth="lg" sx={{ paddingY: 1 }}>
          <Alert severity="error">{storeMessage}</Alert>
        </Container>
      )}
      <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>{store && <Outlet />}</Box>
    </Stack>
  )
}
